import { useEffect, useState } from "react";
import type { ProfileData } from "../data/profiles";
import { generateShareCardBlob } from "./ShareCard";
import { SUPABASE_URL, SUPABASE_ANON_KEY, isSupabaseConfigured } from "../lib/supabase";

interface LeadRow {
  id: string;
  email: string;
  profile: string;
  percentage: number;
  opt_out: boolean;
  variant: string | null;
  utm_source: string | null;
  utm_campaign: string | null;
  exported_at: string | null;
  created_at: string;
}

interface ExportRow extends LeadRow {
  utm_medium: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
  user_agent: string | null;
  answers: number[] | null;
}

interface LeadDetail extends LeadRow {
  utm_medium?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  user_agent?: string | null;
  referrer?: string | null;
  answers?: number[] | null;
  updated_at?: string;
}

interface DetailResponse {
  lead: LeadDetail;
  email: { subject: string | null; html: string | null; text: string | null };
  profileData: ProfileData | null;
}

const STORAGE_KEY = "quiz_admin_password_v1";

async function callAdmin(password: string, body: Record<string, unknown>) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-leads`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ password, ...body }),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

// CSV séparé par `;` (Excel FR) avec BOM UTF-8 pour préserver les accents.
function rowsToCsv(rows: ExportRow[]): string {
  const headers = [
    "email",
    "profile",
    "percentage",
    "opt_out",
    "variant",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "referrer",
    "answers",
    "created_at",
    "exported_at",
  ];
  const escape = (v: unknown): string => {
    if (v === null || v === undefined) return "";
    let s: string;
    if (Array.isArray(v)) s = v.join(",");
    else if (typeof v === "boolean") s = v ? "true" : "false";
    else s = String(v);
    if (s.includes(";") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [
    headers.join(";"),
    ...rows.map((r) =>
      headers.map((h) => escape((r as unknown as Record<string, unknown>)[h])).join(";")
    ),
  ];
  return "﻿" + lines.join("\r\n");
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function AdminScreen() {
  const [password, setPassword] = useState<string>(() => {
    return sessionStorage.getItem(STORAGE_KEY) ?? "";
  });
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);

  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Filtres
  const [search, setSearch] = useState("");
  const [profileFilter, setProfileFilter] = useState<string>("");
  const [onlyNew, setOnlyNew] = useState(false);

  const [exporting, setExporting] = useState(false);

  const handleAuth = async (pwd: string) => {
    setAuthLoading(true);
    setAuthError(null);
    const r = await callAdmin(pwd, { action: "list" });
    setAuthLoading(false);
    if (r.ok && r.data?.success) {
      sessionStorage.setItem(STORAGE_KEY, pwd);
      setAuthed(true);
      setLeads(r.data.leads ?? []);
    } else if (r.status === 401) {
      setAuthError("Mot de passe invalide");
    } else if (r.data?.error === "admin_not_configured") {
      setAuthError("Le secret ADMIN_PASSWORD n'est pas configuré côté Supabase.");
    } else {
      setAuthError(`Erreur ${r.status} — ${r.data?.error ?? "inconnue"}`);
    }
  };

  const refreshLeads = async () => {
    setLoadingLeads(true);
    setError(null);
    const r = await callAdmin(password, { action: "list" });
    setLoadingLeads(false);
    if (r.ok && r.data?.success) {
      setLeads(r.data.leads ?? []);
    } else {
      setError(`Erreur de chargement : ${r.data?.error ?? r.status}`);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);
    const r = await callAdmin(password, { action: "export", onlyNew });
    setExporting(false);
    if (!r.ok || !r.data?.success) {
      setError(`Erreur export : ${r.data?.error ?? r.status}`);
      return;
    }
    const rows: ExportRow[] = r.data.leads ?? [];
    if (rows.length === 0) {
      setError(onlyNew ? "Aucun nouvel email à exporter." : "Aucun lead à exporter.");
      return;
    }
    const csv = rowsToCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const today = new Date().toISOString().slice(0, 10);
    const suffix = onlyNew ? "nouveaux" : "tous";
    downloadBlob(`leads-${suffix}-${today}.csv`, blob);
    // Si on a marqué les nouveaux comme exportés, recharge la liste pour rafraîchir les badges
    if (onlyNew) {
      void refreshLeads();
    }
  };

  const handleResetExport = async (id: string) => {
    const r = await callAdmin(password, { action: "reset_export", id });
    if (r.ok && r.data?.success) {
      void refreshLeads();
    } else {
      setError(`Erreur reset : ${r.data?.error ?? r.status}`);
    }
  };

  // Auto-login si un mot de passe est déjà en session
  useEffect(() => {
    if (password && !authed && !authLoading) {
      handleAuth(password);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger le détail quand un lead est sélectionné
  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoadingDetail(true);
    callAdmin(password, { action: "get", id: selectedId }).then((r) => {
      if (cancelled) return;
      setLoadingDetail(false);
      if (r.ok && r.data?.success) {
        setDetail({
          lead: r.data.lead,
          email: r.data.email,
          profileData: r.data.profileData,
        });
      } else {
        setError(`Erreur lecture lead : ${r.data?.error ?? r.status}`);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedId, password]);

  if (!isSupabaseConfigured) {
    return (
      <div className="min-h-screen bg-background p-8 font-body">
        <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-card p-6 border">
          <h1 className="font-heading font-black text-2xl mb-2">Admin indisponible</h1>
          <p className="text-foreground/80">
            Supabase n'est pas configuré (variables <code>VITE_SUPABASE_URL</code> / <code>VITE_SUPABASE_ANON_KEY</code> manquantes).
          </p>
        </div>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6 font-body">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAuth(password);
          }}
          className="w-full max-w-sm bg-white rounded-2xl shadow-card p-6 border space-y-4"
        >
          <h1 className="font-heading font-black text-2xl text-center">Admin Quiz</h1>
          <p className="text-sm text-muted-foreground text-center">
            Mot de passe administrateur
          </p>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-border bg-white outline-none focus:border-primary"
            placeholder="••••••••"
          />
          {authError && (
            <p className="text-sm text-red-600">{authError}</p>
          )}
          <button
            type="submit"
            disabled={authLoading || !password}
            className="w-full py-3 rounded-full bg-primary text-white font-heading font-bold disabled:opacity-50"
          >
            {authLoading ? "Connexion…" : "Se connecter"}
          </button>
        </form>
      </div>
    );
  }

  const filtered = leads.filter((l) => {
    if (profileFilter && l.profile !== profileFilter) return false;
    if (search && !l.email.toLowerCase().includes(search.toLowerCase())) return false;
    if (onlyNew && l.exported_at) return false;
    return true;
  });

  const newCount = leads.filter((l) => !l.exported_at).length;

  const profileNames: Record<string, string> = {
    visionnaire: "🔭 Visionnaire",
    chill: "😎 Chill",
    stratege: "♟️ Stratège",
    ambitieux: "🚀 Ambitieux",
    securitaire: "🛡️ Sécuritaire",
  };

  return (
    <div className="min-h-screen bg-background font-body">
      <header className="bg-white border-b border-border px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-xl">Admin Quiz — Leads</h1>
          <p className="text-xs text-muted-foreground">
            {leads.length} lead{leads.length > 1 ? "s" : ""} · {newCount} non exporté{newCount > 1 ? "s" : ""} · {filtered.length} affiché{filtered.length > 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handleExport}
            disabled={exporting || loadingLeads}
            className="px-4 py-2 text-sm rounded-full bg-primary text-white font-heading font-bold disabled:opacity-50"
          >
            {exporting
              ? "Export…"
              : onlyNew
                ? `📥 Exporter ${newCount} nouveau${newCount > 1 ? "x" : ""} (CSV)`
                : "📥 Exporter tout (CSV)"}
          </button>
          <button
            onClick={refreshLeads}
            disabled={loadingLeads}
            className="px-3 py-2 text-sm rounded-full border border-border hover:bg-muted disabled:opacity-50"
          >
            {loadingLeads ? "…" : "↻ Rafraîchir"}
          </button>
          <button
            onClick={() => {
              sessionStorage.removeItem(STORAGE_KEY);
              setAuthed(false);
              setPassword("");
              setLeads([]);
            }}
            className="px-3 py-2 text-sm rounded-full border border-border hover:bg-muted"
          >
            Déconnexion
          </button>
        </div>
      </header>

      <div className="px-6 py-3 flex flex-wrap gap-3 items-center bg-white border-b border-border">
        <input
          type="search"
          placeholder="Rechercher un email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] px-3 py-2 rounded-full border border-border outline-none focus:border-primary text-sm"
        />
        <select
          value={profileFilter}
          onChange={(e) => setProfileFilter(e.target.value)}
          className="px-3 py-2 rounded-full border border-border bg-white text-sm"
        >
          <option value="">Tous les profils</option>
          {Object.entries(profileNames).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm font-heading select-none cursor-pointer px-3 py-2 rounded-full border border-border hover:bg-muted">
          <input
            type="checkbox"
            checked={onlyNew}
            onChange={(e) => setOnlyNew(e.target.checked)}
            className="accent-primary"
          />
          <span>Nouveaux uniquement</span>
        </label>
      </div>

      {error && (
        <div className="px-6 py-3 bg-red-50 text-red-700 text-sm border-b border-red-200">
          {error}
        </div>
      )}

      <div className="flex flex-col lg:flex-row">
        {/* Liste */}
        <div className="lg:w-1/2 lg:border-r lg:border-border max-h-[calc(100vh-130px)] overflow-y-auto">
          {filtered.length === 0 && !loadingLeads && (
            <p className="p-6 text-center text-muted-foreground">Aucun lead.</p>
          )}
          <ul className="divide-y divide-border">
            {filtered.map((l) => {
              const isActive = l.id === selectedId;
              return (
                <li key={l.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(l.id)}
                    className={
                      "w-full text-left px-6 py-3 hover:bg-muted transition-colors " +
                      (isActive ? "bg-muted" : "")
                    }
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-heading font-bold truncate flex items-center gap-2">
                          {!l.exported_at && (
                            <span
                              className="inline-block w-2 h-2 rounded-full bg-amber-500 shrink-0"
                              title="Pas encore exporté"
                            />
                          )}
                          <span className="truncate">{l.email}</span>
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {profileNames[l.profile] ?? l.profile} · {l.percentage}%
                          {l.opt_out ? " · ⛔ opt-out" : ""}
                          {l.utm_source ? ` · ${l.utm_source}` : ""}
                          {l.exported_at ? " · ✓ exporté" : ""}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {new Date(l.created_at).toLocaleString("fr-FR", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Détail */}
        <div className="lg:w-1/2 max-h-[calc(100vh-130px)] overflow-y-auto bg-muted/30">
          {!selectedId && (
            <p className="p-6 text-center text-muted-foreground">
              Sélectionne un lead pour voir le contenu envoyé.
            </p>
          )}
          {loadingDetail && (
            <p className="p-6 text-center text-muted-foreground">Chargement…</p>
          )}
          {detail && (
            <LeadDetailView
              detail={detail}
              onResetExport={() => handleResetExport(detail.lead.id)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function LeadDetailView({
  detail,
  onResetExport,
}: {
  detail: DetailResponse;
  onResetExport: () => void;
}) {
  const { lead, email, profileData } = detail;
  const [tab, setTab] = useState<"visual" | "html" | "text" | "raw">("visual");
  const [cardUrl, setCardUrl] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  // Génère le visuel partage à la demande (canvas → blob → object URL)
  useEffect(() => {
    let cancelled = false;
    setCardUrl(null);
    setGenError(null);
    if (tab !== "visual") return;
    if (!profileData) {
      setGenError("Profil inconnu — impossible de générer le visuel.");
      return;
    }
    generateShareCardBlob(profileData, Math.round(lead.percentage)).then((blob) => {
      if (cancelled) return;
      if (!blob) {
        setGenError("Impossible de générer le visuel.");
        return;
      }
      setCardUrl(URL.createObjectURL(blob));
    });
    return () => {
      cancelled = true;
      if (cardUrl) URL.revokeObjectURL(cardUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, profileData, lead.percentage, lead.id]);

  return (
    <div className="p-6 space-y-4">
      <div className="bg-white rounded-2xl shadow-card border p-4">
        <p className="font-heading font-black text-lg">{lead.email}</p>
        <p className="text-xs text-muted-foreground">
          {lead.profile} · {lead.percentage}% match · créé le{" "}
          {new Date(lead.created_at).toLocaleString("fr-FR")}
          {lead.opt_out && <span className="ml-2 text-red-600">opt-out</span>}
        </p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {lead.exported_at ? (
            <>
              <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                ✓ Exporté le {new Date(lead.exported_at).toLocaleString("fr-FR")}
              </span>
              <button
                type="button"
                onClick={onResetExport}
                className="text-xs px-2 py-1 rounded-full border border-border hover:bg-muted"
              >
                Réinitialiser
              </button>
            </>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
              ● Pas encore exporté
            </span>
          )}
        </div>
        {email.subject && (
          <p className="mt-2 text-sm">
            <span className="text-muted-foreground">Sujet : </span>
            <span className="font-heading font-bold">{email.subject}</span>
          </p>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {(["visual", "html", "text", "raw"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={
              "px-4 py-2 rounded-full text-sm font-heading font-bold transition-colors " +
              (tab === t
                ? "bg-primary text-white"
                : "bg-white border border-border hover:bg-muted")
            }
          >
            {t === "visual" && "🖼️ Visuel"}
            {t === "html" && "✉️ Email HTML"}
            {t === "text" && "📝 Texte"}
            {t === "raw" && "🔧 Données"}
          </button>
        ))}
      </div>

      {tab === "visual" && (
        <div className="bg-white rounded-2xl shadow-card border p-4 flex flex-col items-center gap-3">
          {!cardUrl && !genError && <p className="text-muted-foreground py-6">Génération…</p>}
          {genError && <p className="text-red-600 py-6">{genError}</p>}
          {cardUrl && (
            <>
              <img
                src={cardUrl}
                alt="Visuel partage"
                className="max-w-full rounded-xl border border-border"
                style={{ maxHeight: 600 }}
              />
              <a
                href={cardUrl}
                download={`share-${lead.profile}-${lead.email.replace(/[@.]/g, "_")}.png`}
                className="px-4 py-2 rounded-full bg-primary text-white font-heading font-bold text-sm"
              >
                ⬇ Télécharger
              </a>
            </>
          )}
        </div>
      )}

      {tab === "html" && (
        <div className="bg-white rounded-2xl shadow-card border overflow-hidden">
          {email.html ? (
            <iframe
              title="Aperçu email"
              srcDoc={email.html}
              className="w-full"
              style={{ height: 700, border: 0 }}
              sandbox=""
            />
          ) : (
            <p className="p-6 text-muted-foreground">Aucun email HTML disponible.</p>
          )}
        </div>
      )}

      {tab === "text" && (
        <div className="bg-white rounded-2xl shadow-card border p-4">
          {email.text ? (
            <pre className="whitespace-pre-wrap text-sm font-body text-foreground/90 leading-relaxed">
              {email.text}
            </pre>
          ) : (
            <p className="text-muted-foreground">Aucune version texte.</p>
          )}
        </div>
      )}

      {tab === "raw" && (
        <div className="bg-white rounded-2xl shadow-card border p-4 space-y-3">
          <div>
            <p className="text-xs text-muted-foreground">Variante A/B</p>
            <p className="font-heading">{lead.variant ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">UTM</p>
            <p className="font-heading text-sm">
              source={lead.utm_source ?? "—"} · medium={lead.utm_medium ?? "—"} · campaign={lead.utm_campaign ?? "—"}
            </p>
            <p className="font-heading text-sm">
              content={lead.utm_content ?? "—"} · term={lead.utm_term ?? "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Réponses (index)</p>
            <p className="font-heading text-sm">{Array.isArray(lead.answers) ? lead.answers.join(", ") : "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Referrer</p>
            <p className="font-heading text-sm break-all">{lead.referrer ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">User Agent</p>
            <p className="font-heading text-xs break-all">{lead.user_agent ?? "—"}</p>
          </div>
        </div>
      )}
    </div>
  );
}
